import { lazy, Suspense, type ReactNode } from "react";

import { findThemePalette, type ThemeAppearance } from "@cmmd-forge/contracts/theme";

import { useCustomThemes } from "../../hooks/useCustomThemes";
import { useTheme } from "../../hooks/useTheme";
import { resolveThemeHalf, type CustomThemePalette } from "../../themeLibrary";
import { resolveAndApplyTheme } from "../../themePalette";
import { toastManager } from "../ui/toast";
import { useThemeEditorStore } from "./themeEditorStore";

// The host mounts above the router on every page, but the editor body only
// renders once a session opens; lazy-loading keeps the editor out of the
// startup chunk.
const ThemeEditorPanel = lazy(() =>
  import("./ThemeEditorPanel").then((module) => ({ default: module.ThemeEditorPanel })),
);

/**
 * Renders the theme editor above the router. Adapted from upstream's
 * `ThemeEditorHost.tsx` (pingdotgg/t3code, MIT, pin e3e7cc3f). The editor
 * paints its draft on the live app, so it has to outlive the settings route:
 * the point is to walk through threads, panels, and pages while the colors
 * are being tuned. Forge needs no refresh step: the applier effect in
 * `useThemeHalves` re-runs whenever the library changes, so a saved theme
 * repaints on its own.
 */
export function ThemeEditorHost(): ReactNode {
  const session = useThemeEditorStore((store) => store.session);
  const closeThemeEditor = useThemeEditorStore((store) => store.closeThemeEditor);
  const { themeId, setThemeId, resolvedTheme, themeHalves } = useTheme();
  const customThemes = useCustomThemes();

  if (session === null) return null;

  // A seed may name a built-in palette (the active theme can be one), so the
  // lookup falls through the installed library to the shipped palettes. The
  // file model needs an appearance on every palette; a built-in seed takes
  // the appearance the session opened on.
  const findSeed = (id: string | null | undefined): CustomThemePalette | null => {
    if (!id) return null;
    const installed = customThemes.find((theme) => theme.id === id);
    if (installed !== undefined) return installed;
    const builtIn = findThemePalette(id);
    return builtIn ? { ...builtIn, appearance: session.initialAppearance } : null;
  };
  const editingTheme = findSeed(session.editingThemeId);
  const seedTheme = findSeed(session.seedThemeId);
  const appearance: ThemeAppearance = resolvedTheme === "dark" ? "dark" : "light";

  const restorePreview = (): void => {
    resolveAndApplyTheme(resolveThemeHalf(themeId, themeHalves, appearance), appearance);
  };

  const handleSaved = (
    savedTheme: CustomThemePalette,
    result: { readonly created: boolean },
  ): void => {
    if (result.created) {
      setThemeId(savedTheme.id);
      toastManager.add({
        type: "success",
        title: `${savedTheme.name} created`,
        description: "It is now active.",
      });
      return;
    }
    toastManager.add({
      type: "success",
      title: `${savedTheme.name} saved`,
      description: "Your changes are now active.",
    });
  };

  return (
    <Suspense fallback={null}>
      <ThemeEditorPanel
        editingTheme={editingTheme}
        initialAppearance={session.initialAppearance}
        key={session.id}
        onOpenChange={(open) => {
          if (!open) closeThemeEditor();
        }}
        onSaved={handleSaved}
        open
        restorePreview={restorePreview}
        seedName={session.seedName}
        seedTheme={seedTheme}
      />
    </Suspense>
  );
}
